import { IconButton, Table, TableBody, TableCell, TableContainer, TableRow } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import EditIcon from "@material-ui/icons/Edit"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"

import { useTranslation } from "react-i18next"
import { usePageTracking } from "../../../../../hooks"
import { AnalyticsPageCategory, AnalyticsPageType } from "../../../../../models"
import type { PagingOptions } from "../../../../../models"
import { changeRegionPage, deleteRegion, fetchAllRegions, history } from "../../../../../redux"
import type { AppState, AppThunkDispatch, Region, RegionActions } from "../../../../../redux"
import { CreateButton } from "../../../../atoms"
import { CoMaPagingBar, CrudDeleteDialog, CrudTableHeader, PageContainer } from "../../../../molecules"

export interface IRegionManagerOverviewProps {
  regions: Region[]
  pagingOptions: PagingOptions
  fetchRegions: () => void
  deleteRegion: (id: string) => void
  changePage: (page: number) => void
}

function RegionManagerOverviewPageView(props: IRegionManagerOverviewProps): ReactElement {
  const { regions, pagingOptions, fetchRegions, deleteRegion, changePage } = props

  const { t } = useTranslation("contentManager")

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [idToRemove, setIdToRemove] = useState<string | undefined>(undefined)

  usePageTracking(AnalyticsPageCategory.ContentManager, AnalyticsPageType.Region)
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchRegions()
  }, [])

  function onCreateButtonClicked() {
    history.push("/manage/regions/create")
  }

  function onEditButtonClicked(id: string) {
    history.push(`/manage/regions/${id}`)
  }

  function onPageChange(page: number): void {
    if (page < 0 || page >= pagingOptions.totalPages || page === pagingOptions.currentPage) {
      return
    }
    changePage(page)
  }

  function onDeleteButtonClicked(id: string) {
    setDialogOpen(true)
    setIdToRemove(id)
  }

  function onDialogClose(): void {
    setDialogOpen(false)
    setIdToRemove(undefined)
  }

  function onDeleteConfirm(): void {
    if (idToRemove) deleteRegion(idToRemove)
    onDialogClose()
  }

  return (
    <PageContainer id="RegionManagerOverview" noPadding>
      <CreateButton onClick={() => onCreateButtonClicked()} />
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <TableContainer>
        <Table>
          <CrudTableHeader
            headers={[
              { key: "name", label: t("regions.table.region") },
              {
                key: "chapter",
                label: t("regions.table.chapter")
              }
            ]}
            onChange={fetchRegions}
          />
          <TableBody>
            {regions.map((region: Region, index: number) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: TODO fix in later PR
              <TableRow key={index}>
                <TableCell>{region.name}</TableCell>
                <TableCell>{region.chapter?.name}</TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => onEditButtonClicked(region.id as string)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell padding="checkbox">
                  <IconButton onClick={() => onDeleteButtonClicked(region.id as string)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagingOptions.totalPages > 1 && (
        <CoMaPagingBar
          total={pagingOptions.totalPages}
          pageIndex={pagingOptions.currentPage}
          onPageChange={onPageChange}
        />
      )}
      <CrudDeleteDialog open={dialogOpen} onClose={onDialogClose} possibleCascade onConfirm={onDeleteConfirm} />
    </PageContainer>
  )
}

function mapStateToProps(state: AppState) {
  return {
    regions: state.region.regions,
    pagingOptions: state.region.pagingOptions
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<RegionActions>) {
  return {
    fetchRegions: () => dispatch(fetchAllRegions()),
    deleteRegion: (id: string) => dispatch(deleteRegion(id)),
    changePage: (page: number) => dispatch(changeRegionPage(page))
  }
}

export const RegionManagerOverviewPage = connect(mapStateToProps, mapDispatchToProps)(RegionManagerOverviewPageView)
